import * as React from 'react';
import { TableColumns, TextLink, Tooltip, Status, TooltipProvider } from '@teamsnap/snap-ui';
import { currencyGivenPennies } from 'core/utils';
import { DateFormat, DateService } from 'frontend-toolkit';
import { DepositStatus } from 'hooks/financials/reducer';

type ViewTransactionCallback = (
  date: Date,
  depositId: string
) => void;
export const getColumns = (
  viewTransactionCallback: ViewTransactionCallback
): TableColumns => ({
  depositDate: {
    label: 'Deposit Date',
    sort: 'date',
    align: 'left',
    width: '10%',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: DateService.format(
        new Date(row.depositDate),
        DateFormat.DD_MM_YYYY
      ),
    }),
  },
  merchantAccountDisplayName: {
    label: 'Deposit Account',
    sort: 'alpha',
    align: 'left',
    width: '30%',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: (
        <span className="sui-w-max">{row.merchantAccountDisplayName}</span>
      ),
    }),
  },
  type: {
    label: 'Type',
    sort: 'alpha',
    align: 'left',
    width: '10%',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: row.type ?? EMPTY_VALUE(),
    }),
  },
  status: {
    label: 'Status',
    sort: 'alpha',
    align: 'left',
    width: '10%',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: statusComponents[row.status] ?? EMPTY_VALUE(),
    }),
  },
  grossAmount: {
    label: 'Gross',
    headerProps: {
      tooltip: (
        <>
          Gross is the total amount of all payment transactions{' '}
          <strong className=" sui-font-bold">before</strong> online fees
          have been deducted
        </>
      ),
    },
    sort: 'numeric',
    align: 'right',
    width: '10%',
    getBodyProps: (row) => ({
      type: 'custom',
      children: row.grossAmount
        ? currencyGivenPennies(row.grossAmount)
        : EMPTY_VALUE(),
    }),
  },
  fees: {
    label: 'Fees',
    headerProps: {
      tooltip:
        'Online fees are deducted for every online payment transactions',
    },
    sort: 'numeric',
    align: 'right',
    width: '10%',

    getBodyProps: (row) => ({
      type: 'custom',
      children: row.fees ? currencyGivenPennies(row.fees) : EMPTY_VALUE(),
    }),
  },
  netAmount: {
    label: 'Net',
    headerProps: {
      tooltip: (
        <>
          Net is the total amount of all payment transactions{' '}
          <strong className="sui-font-black">after</strong> online fees have
          been deducted
        </>
      ),
    },
    sort: 'numeric',
    align: 'right',
    width: '10%',

    getBodyProps: (row) => ({
      type: 'custom',
      children: row.netAmount
        ? currencyGivenPennies(row.netAmount)
        : EMPTY_VALUE(),
    }),
  },
  actions: {
    label: 'Actions',
    sort: 'alpha',
    align: 'left',
    width: '200px',
    getBodyProps: (row) => ({
      type: 'custom',
      children: (
        <TextLink
          href="#"
          onClick={() =>
            viewTransactionCallback(
              new Date(row.depositDate),
              row.depositId
            )
          }
        >
          View Transactions
        </TextLink>
      ),
    }),
  },
});

const EMPTY_VALUE = () => <>&#8212;</>;
const statusComponents = {
  [DepositStatus.PAID]: <Status state="success" text="Paid" />,
  [DepositStatus.FAILED]: <TooltipProvider>
            <Tooltip
            align="center"
            content={`The associated payout was unsuccessful, please reach out to Payments@TeamSnap.com for assistance.
              The failed amount will be included in your next successful deposit.
              Please note that failed deposits are not included in Gross/Net/Fees totals at the top of this page.`}
            side="top"
            >
              <span>
                <Status state="negative" text="Failed" />
              </span>
            </Tooltip>
                          </TooltipProvider>,
  // enum from db schema below not currently in use 11/15/24
  [DepositStatus.PENDING]: <Status state="inactive" text="Pending" />,
  [DepositStatus.CANCELLED]: <Status state="inactive" text="Cancelled" />,
  [DepositStatus.IN_TRNASIT]: <Status state="inactive" text="In Transit" />,
};

export const CSV_HEADERS = [
  'Deposit Date',
  'Deposit account',
  'Deposit Account',
  'Type',
  'Gross',
  'Fees',
  'Net',
];
